import RatingPanel from "./RatingPanel";
import { TARGET_PER_ITEM_LABEL, targetMemorizeSeconds } from "../lib/targetPace";

const fmt = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const signed = (n) => (n > 0 ? `+${Math.round(n)}` : `${Math.round(n)}`);

// The rating, what it means, and the working behind it — results screen.
export const RatingSummary = ({
  results,
  mode,
  itemsCount,
  difficulty,
  memorizeAllowanceSeconds,
  accent = "text-secondary",
}) => {
  const rating = results.rating ?? results.performance_score;
  const recalled = results.correct_credit ?? results.correct_count ?? 0;
  const total = results.total_items ?? itemsCount;
  const displayRecalled = Math.round(recalled * 10) / 10;
  const targetHint = `Target pace for ${itemsCount} items is ${fmt(
    targetMemorizeSeconds(mode, itemsCount)
  )} (${TARGET_PER_ITEM_LABEL[mode] || "target pace"}). Beating it adds to your rating; going slower takes away.`;

  return (
    <div className="text-center mb-8 p-8 bg-muted border border-border" data-testid="rating-summary">
      <div className={`font-mono text-6xl mb-2 ${accent}`} data-testid="final-score">
        {Math.round(rating)}
      </div>
      <div className="flex items-center justify-center gap-2">
        <p className="text-muted-foreground">
          Rating <span className="text-muted-foreground/60">/ 150</span>
        </p>
        <RatingPanel
          mode={mode}
          itemsCount={itemsCount}
          difficulty={difficulty}
          memorizeAllowanceSeconds={memorizeAllowanceSeconds}
        />
      </div>
      <p className="text-sm text-foreground/80 mt-1" data-testid="rating-band">
        {results.rating_band}
      </p>

      <div
        className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-mono text-xs sm:text-sm text-muted-foreground"
        data-testid="rating-working"
      >
        <span data-testid="recalled-count">
          Recalled{" "}
          <span className="text-green-500 font-bold">
            {displayRecalled} / {total}
          </span>
        </span>
        {mode !== "working_memory" && (
          <>
            <span title={targetHint} data-testid="memorise-working">
              <span className="text-lime-400/80">Memorise</span> {fmt(results.memorize_seconds)}
              {results.speed_adjusted && (
                <span className="ml-1 text-foreground/70">· {signed(results.mem_bonus)}</span>
              )}
            </span>
            <span title={targetHint} data-testid="recall-working">
              <span className="text-blue-400/80">Recall</span> {fmt(results.time_taken_seconds)}
              {results.speed_adjusted && (
                <span className="ml-1 text-foreground/70">· {signed(results.recall_bonus)}</span>
              )}
            </span>
          </>
        )}
        {results.points != null && (
          <span data-testid="session-points">
            <span className="text-primary/80">Points</span> {results.points}
          </span>
        )}
      </div>
      {results.speed_adjusted === false && mode !== "working_memory" && (
        <p className="text-xs text-amber-400/80 mt-2" data-testid="no-speed-adjustment">
          No memorisation time recorded — scored without a speed adjustment
        </p>
      )}
    </div>
  );
};

export default RatingSummary;
